import React from 'react';
import { X, Building2, MapPin, Activity, TrendingUp, Globe, Phone, Mail, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import type { CompanyResult } from '../types';
import { useAuth } from '../context/AuthContext';
import { Lock } from 'lucide-react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

interface CompanyDetailModalProps {
    isOpen: boolean;
    company: CompanyResult | null;
    onClose: () => void;
}

const CompanyDetailModal: React.FC<CompanyDetailModalProps> = ({ isOpen, company, onClose }) => {
    const { user } = useAuth();
    const isPro = user?.rol === 'pro' || user?.rol === 'admin';

    if (!isOpen || !company) return null;

    const data = company.data;
    const stareColor = company.stare?.toLowerCase().includes('activ') || company.stare?.toLowerCase().includes('functioneaza')
        ? 'text-green-600'
        : 'text-red-600';

    // Prepare chart data if 2023 exists
    const chartData = data.date_financiare_2024 && data.date_financiare_2023 ? [
        {
            name: 'Cifra de Afaceri',
            2023: parseFloat(typeof data.date_financiare_2023.cifra_de_afaceri_neta === 'string' ? data.date_financiare_2023.cifra_de_afaceri_neta.replace(/,/g, '') : data.date_financiare_2023.cifra_de_afaceri_neta),
            2024: parseFloat(typeof data.date_financiare_2024.cifra_de_afaceri_neta === 'string' ? data.date_financiare_2024.cifra_de_afaceri_neta.replace(/,/g, '') : data.date_financiare_2024.cifra_de_afaceri_neta),
        },
        {
            name: 'Profit Net',
            2023: parseFloat(typeof data.date_financiare_2023.profit_net === 'string' ? data.date_financiare_2023.profit_net.replace(/,/g, '') : data.date_financiare_2023.profit_net),
            2024: parseFloat(typeof data.date_financiare_2024.profit_net === 'string' ? data.date_financiare_2024.profit_net.replace(/,/g, '') : data.date_financiare_2024.profit_net),
        }
    ] : [];

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-start justify-between p-6 border-b border-gray-100">
                    <div className="flex items-start gap-4 flex-1">
                        <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
                            <Building2 className="w-8 h-8" />
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-gray-900 mb-1">{company.denumire}</h2>
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                                <span className="px-3 py-1 bg-gray-100 rounded-full font-mono text-gray-700">
                                    CUI: {company.cui}
                                </span>
                                <span className={`px-3 py-1 rounded-full font-semibold ${stareColor.includes('green') ? 'bg-green-100' : 'bg-red-100'
                                    } ${stareColor}`}>
                                    {company.stare || 'Status necunoscut'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-6">
                        {/* Detalii de Identificare */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <section>
                                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Building2 className="w-5 h-5 text-blue-600" />
                                    Detalii de Identificare
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <InfoItem label="Denumire" value={data.DENUMIRE} />
                                    <InfoItem label="CUI" value={data.CUI} />
                                    <InfoItem label="Nr. Reg. Com." value={data.COD_INMATRICULARE} />
                                    <InfoItem label="EUID" value={data.EUID} />
                                    <InfoItem label="Forma Juridică" value={data.FORMA_JURIDICA} />
                                    <InfoItem label="Data Înmatriculare" value={data.DATA_INMATRICULARE} />
                                </div>
                            </section>

                            {(data.WEB || data.EMAIL || data.TELEFON || company.adresa) && (
                                <section>
                                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <Globe className="w-5 h-5 text-blue-600" />
                                        Contact & Adresă
                                    </h3>
                                    <div className="space-y-4">
                                        {company.adresa && (
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <div className="flex items-start gap-2">
                                                    <MapPin className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                                                    <p className="text-sm text-gray-900">{company.adresa}</p>
                                                </div>
                                            </div>
                                        )}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {data.WEB && (
                                                <div className="flex items-center gap-2">
                                                    <Globe className="w-4 h-4 text-gray-400" />
                                                    <a href={data.WEB} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate">
                                                        {data.WEB}
                                                    </a>
                                                </div>
                                            )}
                                            {data.EMAIL && (
                                                <div className="flex items-center gap-2">
                                                    <Mail className="w-4 h-4 text-gray-400" />
                                                    <a href={`mailto:${data.EMAIL}`} className="text-sm text-blue-600 hover:underline truncate">
                                                        {data.EMAIL}
                                                    </a>
                                                </div>
                                            )}
                                            {data.TELEFON && (
                                                <div className="flex items-center gap-2">
                                                    <Phone className="w-4 h-4 text-gray-400" />
                                                    <span className="text-sm text-gray-900">{data.TELEFON}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </section>
                            )}
                        </div>

                        {/* Activități CAEN */}
                        {data.activitati_caen && Array.isArray(data.activitati_caen) && data.activitati_caen.length > 0 && (
                            <section>
                                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Activity className="w-5 h-5 text-blue-600" />
                                    Domenii de Activitate
                                </h3>
                                <div className="space-y-2">
                                    {data.activitati_caen.slice(0, 3).map((caen: any, idx: number) => (
                                        <div key={idx} className="p-3 bg-blue-50/50 rounded-lg border border-blue-100 flex items-start gap-3">
                                            <span className="px-2 py-0.5 bg-blue-600 text-white rounded text-xs font-bold font-mono shrink-0 mt-0.5">
                                                {caen.cod_caen || caen.cod || caen.code}
                                            </span>
                                            <div>
                                                <p className="text-sm text-gray-800 leading-snug">
                                                    {caen.descriere}
                                                </p>
                                                {caen.versiune && (
                                                    <span className="text-xs text-gray-500">Rev. {caen.versiune}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}


                        {/* Date Financiare Comparative */}
                        {data.date_financiare_2024 && (
                            <section>
                                <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-blue-600" />
                                    Analiză Financiară: 2024 vs 2023
                                </h3>

                                {data.date_financiare_2023 ? (
                                    <>
                                        <div className="grid grid-cols-1 gap-8 mb-8">
                                            {/* Evolution Chart */}
                                            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm h-80">
                                                <h4 className="text-sm font-semibold text-gray-600 mb-4 text-center">Evoluție Principală (RON)</h4>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} tickFormatter={(value) => new Intl.NumberFormat('ro-RO', { notation: 'compact' }).format(value)} />
                                                        <Tooltip
                                                            cursor={{ fill: '#F3F4F6' }}
                                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                                            formatter={(value: any) => (value !== undefined && value !== null ? new Intl.NumberFormat('ro-RO').format(value) + ' RON' : '')}
                                                        />
                                                        <Legend iconType="circle" />
                                                        <Bar dataKey="2023" name="2023" fill="#94A3B8" radius={[4, 4, 0, 0]} barSize={40} />
                                                        <Bar dataKey="2024" name="2024" fill="#2563EB" radius={[4, 4, 0, 0]} barSize={40} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>

                                            {/* Comparative Table */}
                                            <div className="overflow-x-auto rounded-xl border border-gray-200">
                                                <table className="w-full text-sm text-left min-w-[600px] sm:min-w-0">
                                                    <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                                                        <tr>
                                                            <th className="py-3 px-2 sm:px-4 text-xs sm:text-sm">Indicator</th>
                                                            <th className="py-3 px-2 sm:px-4 text-right text-xs sm:text-sm">2023</th>
                                                            <th className="py-3 px-2 sm:px-4 text-right text-blue-700 font-bold text-xs sm:text-sm">2024</th>
                                                            <th className="py-3 px-2 sm:px-4 text-center text-xs sm:text-sm">Evoluție</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        <ComparativeRow
                                                            label="Cifra de Afaceri"
                                                            val2023={data.date_financiare_2023.cifra_de_afaceri_neta}
                                                            val2024={data.date_financiare_2024.cifra_de_afaceri_neta}
                                                        />
                                                        <ComparativeRow
                                                            label="Profit Net"
                                                            val2023={data.date_financiare_2023.profit_net}
                                                            val2024={data.date_financiare_2024.profit_net}
                                                            highlight
                                                        />
                                                        <ComparativeRow
                                                            label="Datorii"
                                                            val2023={data.date_financiare_2023.datorii}
                                                            val2024={data.date_financiare_2024.datorii}
                                                            inverse // Growing debts usually red
                                                        />
                                                        <ComparativeRow
                                                            label="Active Totale"
                                                            val2023={parseFloat(data.date_financiare_2023.active_imobilizate_total || 0) + parseFloat(data.date_financiare_2023.active_circulante_total_din_care || 0)}
                                                            val2024={parseFloat(data.date_financiare_2024.active_imobilizate_total || 0) + parseFloat(data.date_financiare_2024.active_circulante_total_din_care || 0)}
                                                        />
                                                        <ComparativeRow
                                                            label="Angajați"
                                                            val2023={data.date_financiare_2023.numar_mediu_de_salariati}
                                                            val2024={data.date_financiare_2024.numar_mediu_de_salariati}
                                                            isCurrency={false}
                                                        />
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="mb-6 p-4 bg-yellow-50 text-yellow-800 rounded-lg border border-yellow-100 text-sm">
                                        {(() => {
                                            if (data.DATA_INMATRICULARE) {
                                                const match = data.DATA_INMATRICULARE.match(/(\d{4})/);
                                                if (match) {
                                                    const year = parseInt(match[1]);
                                                    if (year >= 2023) {
                                                        return `Societatea a fost înființată în anul ${year} și nu există date financiare anterioare disponibile.`;
                                                    }
                                                }
                                            }
                                            return "Datele pentru anul 2023 nu sunt disponibile momentan. Se afișează doar situația pe 2024.";
                                        })()}
                                    </div>
                                )}


                                {/* 2024 Summary Cards (Still useful for quick glance) */}
                                {!data.date_financiare_2023 && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <FinancialCard
                                            label="Cifra de Afaceri Netă"
                                            value={data.date_financiare_2024.cifra_de_afaceri_neta}
                                            color="blue"
                                        />
                                        <FinancialCard
                                            label="Profit Net"
                                            value={data.date_financiare_2024.profit_net}
                                            color="green"
                                        />
                                        <FinancialCard
                                            label="Datorii"
                                            value={data.date_financiare_2024.datorii}
                                            color="red"
                                        />
                                    </div>
                                )}
                            </section>
                        )}


                        {/* Indicatori Financiari Calculați (PRO Features) */}
                        <section>
                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Activity className="w-5 h-5 text-purple-600" />
                                Indicatori de Performanță
                                {!isPro && (
                                    <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-bold uppercase tracking-wider">
                                        PRO
                                    </span>
                                )}
                            </h3>

                            {!isPro ? (
                                <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center text-center">
                                    <div className="bg-white p-4 rounded-full shadow-md mb-4">
                                        <Lock className="w-8 h-8 text-purple-600" />
                                    </div>
                                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                                        Acces Exclusiv la Analiză Financiară
                                    </h4>
                                    <p className="text-gray-600 mb-6 max-w-md">
                                        Deblocați tabloul de bord complet cu indicatori precum EBITDA, ROE, marje de profit și analiză de solvabilitate.
                                    </p>
                                    <button
                                        onClick={() => window.location.href = '/abonamente'}
                                        className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors shadow-lg"
                                    >
                                        Activează Abonamentul PRO
                                    </button>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <FinancialCard
                                        label="EBITDA"
                                        value={data.EBITDA}
                                        color="purple"
                                        subValue={data.variatii_indicatori?.EBITDA_var}
                                    />
                                    <FinancialCard
                                        label="Marja Profit Net"
                                        value={data.Marja_Profit_Net}
                                        color="blue"
                                        isPercentage
                                        subValue={data.variatii_indicatori?.Marja_Profit_Net_var}
                                    />
                                    <FinancialCard
                                        label="ROE (Rentabilitate Capital)"
                                        value={data.ROE}
                                        color="green"
                                        isPercentage
                                        subValue={data.variatii_indicatori?.ROE_var}
                                    />
                                    <FinancialCard
                                        label="Grad de Îndatorare"
                                        value={data.Rata_Datoriilor}
                                        color={data.Rata_Datoriilor > 70 ? 'red' : 'green'}
                                        isPercentage
                                        subValue={data.variatii_indicatori?.Rata_Datoriilor_var}
                                        inverseTrend
                                    />
                                </div>
                            )}

                            {/* Concluzii Automate */}
                            {data.Analiza_Financiara_Text && (
                                <div className="mt-6 p-5 bg-gradient-to-r from-indigo-50 to-blue-50 border border-blue-100 rounded-xl">
                                    <h4 className="text-sm font-bold text-indigo-900 mb-3 flex items-center gap-2 uppercase tracking-wide">
                                        <TrendingUp className="w-4 h-4" />
                                        Concluzii Automate & Evoluție
                                    </h4>
                                    <div className="text-indigo-900 text-sm leading-relaxed font-medium">
                                        {data.Analiza_Financiara_Text}
                                    </div>
                                </div>
                            )}
                        </section>

                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper Components

const ComparativeRow: React.FC<{
    label: string,
    val2023: any,
    val2024: any,
    isCurrency?: boolean,
    highlight?: boolean,
    inverse?: boolean
}> = ({ label, val2023, val2024, isCurrency = true, highlight = false, inverse = false }) => {
    const parse = (v: any) => {
        if (typeof v === 'number') return v;
        if (!v) return 0;
        return parseFloat(v.toString().replace(/,/g, ''));
    };

    const v23 = parse(val2023);
    const v24 = parse(val2024);

    // Variation
    let variation = 0;
    let hasVariation = false;
    if (v23 !== 0) {
        variation = ((v24 - v23) / Math.abs(v23)) * 100;
        hasVariation = true;
    }

    const format = (v: number) => {
        if (isCurrency) return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON', maximumFractionDigits: 0 }).format(v);
        return new Intl.NumberFormat('ro-RO').format(v);
    };

    const isPositive = variation > 0;
    // Determine color based on inverse flag (e.g. debt increase is bad)
    const trendColor = inverse
        ? (isPositive ? 'text-red-600' : 'text-green-600')
        : (isPositive ? 'text-green-600' : 'text-red-600');

    const Icon = isPositive ? ArrowUpRight : (variation < 0 ? ArrowDownRight : Minus);

    return (
        <tr className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${highlight ? 'bg-blue-50/30' : ''}`}>
            <td className="py-3 px-2 sm:px-4 font-medium text-gray-700 text-xs sm:text-sm">{label}</td>
            <td className="py-3 px-2 sm:px-4 text-right text-gray-500 font-mono text-xs sm:text-sm">{format(v23)}</td>
            <td className="py-3 px-2 sm:px-4 text-right font-bold text-gray-900 font-mono text-xs sm:text-sm">{format(v24)}</td>
            <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm">
                {hasVariation ? (
                    <div className={`flex items-center justify-center gap-1 font-bold ${trendColor}`}>
                        <Icon className="w-3 h-3" />
                        {Math.abs(variation).toFixed(1)}%
                    </div>
                ) : (
                    <div className="text-center text-gray-300">-</div>
                )}
            </td>
        </tr>
    );
}

const InfoItem: React.FC<{ label: string; value: any }> = ({ label, value }) => {
    if (!value) return null;

    return (
        <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</dt>
            <dd className="text-sm text-gray-900 font-medium truncate">{value}</dd>
        </div>
    );
};

const FinancialCard: React.FC<{
    label: string;
    value: any;
    color: string;
    isPercentage?: boolean;
    subValue?: number;
    inverseTrend?: boolean;
}> = ({ label, value, color, isPercentage, subValue, inverseTrend }) => {
    if (value === null || value === undefined || value === '') return null;

    const colorClasses = {
        blue: 'bg-blue-50 text-blue-700 border-blue-100',
        green: 'bg-green-50 text-green-700 border-green-100',
        purple: 'bg-purple-50 text-purple-700 border-purple-100',
        red: 'bg-red-50 text-red-700 border-red-100',
    }[color] || 'bg-gray-50 text-gray-700 border-gray-100';

    let formattedValue = '-';
    let numericValue = value;

    if (typeof value === 'string') {
        const cleanVal = value.replace(/,/g, '');
        const parsed = parseFloat(cleanVal);
        if (!isNaN(parsed)) {
            numericValue = parsed;
        } else {
            formattedValue = value;
        }
    }

    if (typeof numericValue === 'number' && !isNaN(numericValue)) {
        if (isPercentage) {
            formattedValue = `${numericValue.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
        } else {
            formattedValue = new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON', maximumFractionDigits: 0 }).format(numericValue);
        }
    }

    return (
        <div className={`p-4 rounded-xl border ${colorClasses} flex flex-col justify-between`}>
            <div>
                <p className="text-xs font-medium uppercase tracking-wide mb-1 opacity-75">{label}</p>
                <div className="flex items-baseline gap-2">
                    <p className="text-lg font-bold">{formattedValue}</p>
                    {subValue !== undefined && subValue !== null && (
                        <span className={`text-xs font-bold flex items-center ${(inverseTrend ? -subValue : subValue) >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                            {subValue > 0 ? '+' : ''}{subValue.toFixed(1)}%
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CompanyDetailModal;
